import { useLayoutEffect, useRef, useState } from "react";
import { alpha, Box, Paper, Tab, Tabs, Typography } from "@mui/material";
import { ResourceContextProvider, Title } from "react-admin";
import { useSearchParams } from "react-router-dom";
import { settings } from "app/settings";
import { getPermissions } from "auth/security/securityUtils";
import { checkPermission } from "server/security/utils";

// Name of the URL search param holding the active tab.
export const DEFAULT_TAB_PARAM = "tab";

// Each tab is rendered as a card instead of the default underlined tab.
// Width is set in TabsPage (every card as wide as the one with the longest label);
// minWidth keeps short labels readable.
const tabCardStyle = {
  minHeight: 96,
  minWidth: 150,
  maxWidth: "none",
  px: 2,
  py: 1.5,
  gap: 0.5,
  textTransform: "none",
  fontSize: "0.9rem",
  fontWeight: 600,
  lineHeight: 1.3,
  borderRadius: 2,
  border: theme => `1px solid ${theme.palette.divider}`,
  color: "text.secondary",
  transition: "all .2s ease",
  "&:hover": {
    borderColor: "primary.light",
    backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
    transform: "translateY(-2px)",
  },
  "&.Mui-selected": {
    color: "primary.main",
    fontWeight: 700,
    borderWidth: 2,
    borderColor: "primary.main",
    backgroundColor: theme => alpha(theme.palette.primary.main, 0.08),
    boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.22)}`,
  },
};

// Prevent non-latin characters as URL.
const isUrlSafePath = pathname =>
  ![...pathname].some(char => char.charCodeAt(0) > 127) && !/%[89A-Fa-f][0-9A-Fa-f]/.test(pathname);

/**
 * A page whose content is split into card-style tabs.
 *
 * No tab is preselected — nothing is fetched until the user picks one, which is the whole
 * point of the pattern when every tab is a <List> hitting its own endpoint.
 *
 * @param title       document title; rendered only when the active tab is not a react-admin
 *                    resource list (a <List> sets its own title)
 * @param tabs        [{ key, label, icon, resource?, permission?, content }] — `resource` is
 *                    the full react-admin resource name; when set, `content` is wrapped in a
 *                    ResourceContextProvider so a <List> from another module renders
 *                    against the right API path. A tab declaring `permission` is hidden from
 *                    users who lack it: resource registration in auth/App.js applies the same
 *                    check, so without this a tab would render a <List> against a resource
 *                    that was never registered — firing its request and 404-ing on row click.
 *                    Tabs with no `permission` are always shown.
 * @param urlParam    search param holding the active tab, so returning from a details/edit
 *                    page restores it; pass a falsy value to keep the tab in local state
 * @param placeholder text shown while no tab is selected
 */
const TabsPage = ({ title, tabs = [], urlParam = DEFAULT_TAB_PARAM, placeholder }) => {
  // A resource `name` is also its route, so a Bulgarian label there ends up in the URL.
  if (process.env.NODE_ENV !== "production" && !isUrlSafePath(window.location.pathname)) {
    console.error(
      `TabsPage mounted at non-latin URL "${window.location.pathname}". ` +
        "Set the resource `name` from a *ServicePath constant and keep the label in `title`.",
    );
  }

  const [searchParams, setSearchParams] = useSearchParams();
  const [localKey, setLocalKey] = useState(null);

  // Read synchronously (like DoctoralSeatApprovals/Page.js) rather than via usePermissions,
  // whose loading pass would flash the gated tabs in after the rest of the strip.
  const userPermissions = getPermissions();
  const visibleTabs = tabs.filter(tab => checkPermission(userPermissions, tab.permission));

  // Equal-width cards: render once at natural width (flex "0 0 auto"), measure the widest card
  // before paint, then give every card exactly that width, with an equal gap in a centered, wrapping row.
  // A wrapped row then keeps the same card width instead of stretching its cards. Re-measured
  // only when the set of tabs changes.
  const tabRefs = useRef({});
  const tabsKey = visibleTabs.map(tab => tab.key).join("|");
  const [measured, setMeasured] = useState({ tabsKey: null, width: 0 });
  const isMeasured = measured.tabsKey === tabsKey;

  useLayoutEffect(() => {
    if (isMeasured) return;
    const widths = visibleTabs
      .map(tab => tabRefs.current[tab.key]?.getBoundingClientRect().width ?? 0)
      .filter(width => width > 0);
    setMeasured({ tabsKey, width: widths.length ? Math.ceil(Math.max(...widths)) : 0 });
    // visibleTabs is a new array every render, so this effect re-runs each time — it returns at
    // once while isMeasured, so that is free and needs no eslint-disable.
  }, [isMeasured, tabsKey, visibleTabs]);

  const activeKey = urlParam ? searchParams.get(urlParam) : localKey;
  const activeTab = visibleTabs.find(tab => tab.key === activeKey);

  const onTabChange = (_, key) =>
    urlParam ? setSearchParams({ [urlParam]: key }, { replace: true }) : setLocalKey(key);

  const renderContent = () => {
    if (!activeTab) {
      return (
        <Paper>
          <Box py={4}>
            <Typography
              variant="inherit"
              align="center"
              color={"secondary"}
            >
              {placeholder}
            </Typography>
          </Box>
        </Paper>
      );
    }

    if (!activeTab.resource) return activeTab.content;

    return <ResourceContextProvider value={activeTab.resource}>{activeTab.content}</ResourceContextProvider>;
  };

  return (
    <>
      {!activeTab?.resource && <Title title={title} />}
      <Tabs
        value={activeTab?.key ?? false}
        onChange={onTabChange}
        variant="fullWidth"
        TabIndicatorProps={{ sx: { display: "none" } }}
        sx={{
          mb: 2,
          // Cards keep one fixed width (see the Tab below) and the same gap between them in every
          // row; each row's free space goes to its edges, so the rows - also a wrapped one - are centered.
          "& .MuiTabs-flexContainer": {
            gap: 2,
            flexWrap: "wrap",
            justifyContent: isMeasured ? "center" : "flex-start",
          },
          // room for the hover lift and the selected card shadow
          "& .MuiTabs-scroller": { py: 1.5, px: 0.5 },
        }}
      >
        {visibleTabs.map(tab => (
          <Tab
            key={tab.key}
            ref={el => {
              tabRefs.current[tab.key] = el;
            }}
            value={tab.key}
            icon={tab.icon}
            iconPosition="top"
            label={tab.label}
            // Measuring pass: natural width. Then every card is exactly as wide as the widest one;
            // neither pass grows or shrinks (overrides fullWidth's flex "1 1 0"). maxWidth keeps a
            // card on screen when it is wider than the container.
            sx={{
              ...tabCardStyle,
              flex: isMeasured ? `0 0 ${measured.width}px` : "0 0 auto",
              maxWidth: "100%",
            }}
          />
        ))}
      </Tabs>
      {renderContent()}
    </>
  );
};

export default TabsPage;

/**
 * Builds the `tabs` array from a list of react-admin resource objects (the `resources`
 * export of each feature module). The resources are registered with `isMenu: false`, so
 * their `icon`/`title` are free to describe the tab; `tabList` holds the real <List>
 * (`list` itself points at a RedirectToTab bounce).
 *
 * The tab key defaults to the last segment of the resource name — declare `tabKey` on the
 * resource when that segment isn't unique across the group (e.g. `candidate/paid/candidacies`
 * and `candidate/regular/candidacies` both end in "candidacies").
 *
 * `tenancies` and `permission` are honoured the same way auth/App.js honours them when it
 * registers the resources, so a tab can never outlive its own resource registration. Tenant
 * is static, so it is filtered here; permission is per-user and filtered by TabsPage.
 */
export const tabsFromResources = resources =>
  resources
    .filter(resource => resource.tenancies === undefined || resource.tenancies.includes(settings.tenant))
    .map(({ name, tabKey, title, icon, permission, tabList }) => ({
      key: tabKey ?? name.split("/").pop(),
      label: title,
      icon,
      resource: name,
      permission,
      content: tabList,
    }));
